import { useEffect, useCallback, useRef, useState } from 'react';
import { NotificationAPI } from '@/apis/NotificationAPI';
import { ConfigurationAPI } from '@/apis/ConfigurationAPI';
import { isValidHttpUrl } from '@/utils';
import { useUserExperienceConfig } from '@/components/ConfigContext';
import Cookies from 'js-cookie';
export const useRbacNotifications = () => {
    const subscriptionRef = useRef(null);
    const [isUserSubscribed, setIsUserSubscribed] = useState(false);
    const vapidKeyRef = useRef(null);
    const [hasVapidKey, setHasVapidKey] = useState(false);
    const getUxConfig = useUserExperienceConfig();
    useEffect(() => {
        getUxConfig()
            .then((config) => {
            const key = config['RBAC_UI_VAPID_PUBLIC_KEY'];
            vapidKeyRef.current = key || null;
            setHasVapidKey(Boolean(key));
        })
            .catch(() => { });
    }, [getUxConfig]);
    const initializeNotificationSystem = useCallback(async () => {
        try {
            const supported = 'serviceWorker' in navigator &&
                'PushManager' in globalThis.window &&
                'Notification' in globalThis.window;
            if (!supported) {
                return;
            }
            const rawBasePath = process.env.REACT_APP_BASE_PATH || '/';
            let basePath = rawBasePath;
            while (basePath.length > 1 && basePath.endsWith('/')) {
                basePath = basePath.slice(0, -1);
            }
            const swPath = `${basePath === '/' ? '' : basePath}/rbac-sw.js`;
            const registration = await navigator.serviceWorker.register(swPath);
            await navigator.serviceWorker.ready;
            const existingSubscription = await registration.pushManager.getSubscription();
            if (existingSubscription) {
                subscriptionRef.current = existingSubscription;
                setIsUserSubscribed(true);
            }
        }
        catch (error) {
            console.error('[RBAC Notifications] Initialization failed:', error);
        }
    }, []);
    const getUserId = useCallback(() => {
        try {
            const user = Cookies.get('user');
            const userData = user ? JSON.parse(user) : null;
            if (userData?.id) {
                return String(userData.id);
            }
            return null;
        }
        catch (error) {
            console.error('[RBAC Notifications] Failed to parse user cookie:', error);
            return null;
        }
    }, []);
    const enablePushNotification = useCallback(async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setIsUserSubscribed(false);
                return { ok: false, reason: 'permission_denied' };
            }
            const userId = getUserId();
            if (!userId) {
                setIsUserSubscribed(false);
                return { ok: false, reason: 'no_user' };
            }
            const vapidPublicKey = vapidKeyRef.current;
            if (!vapidPublicKey) {
                setIsUserSubscribed(false);
                return { ok: false, reason: 'no_vapid_key' };
            }
            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();
            const sub = existing ||
                (await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertVapidKey(vapidPublicKey)
                }));
            subscriptionRef.current = sub;
            setIsUserSubscribed(true);
            syncSubscription(sub, true, userId).catch((error) => {
                console.error('[RBAC Notifications] Sync failed:', error);
            });
            return { ok: true };
        }
        catch (error) {
            console.error('[RBAC Notifications] Enable failed:', error);
            setIsUserSubscribed(false);
            return { ok: false, reason: 'subscription_failed' };
        }
    }, [getUserId]);
    const disablePushNotification = useCallback(async () => {
        try {
            const userId = getUserId();
            if (!userId) {
                console.warn('[RBAC Notifications] No user ID found');
                return false;
            }
            const registration = await navigator.serviceWorker.ready;
            const existingSubscription = await registration.pushManager.getSubscription();
            const subToDisable = subscriptionRef.current || existingSubscription;
            if (subToDisable) {
                await subToDisable.unsubscribe();
                await syncSubscription(subToDisable, false, userId);
                subscriptionRef.current = null;
                setIsUserSubscribed(false);
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('[RBAC Notifications] Disable failed:', error);
            return false;
        }
    }, [getUserId]);
    useEffect(() => {
        initializeNotificationSystem();
    }, [initializeNotificationSystem]);
    useEffect(() => {
        const autoPrompt = async () => {
            try {
                if (typeof globalThis === 'undefined')
                    return;
                if (!('Notification' in globalThis))
                    return;
                if (!globalThis.isSecureContext &&
                    globalThis.location.hostname !== 'localhost')
                    return;
                if (Notification.permission !== 'default')
                    return;
                const userId = getUserId();
                if (!userId)
                    return;
                const prompted = sessionStorage.getItem('rbac_push_prompt_shown');
                if (prompted === '1')
                    return;
                sessionStorage.setItem('rbac_push_prompt_shown', '1');
                await navigator.serviceWorker.ready;
                await enablePushNotification();
            }
            catch (e) {
                console.warn('[RBAC Notifications] Auto-prompt failed:', e);
            }
        };
        autoPrompt();
    }, [getUserId, enablePushNotification]);
    return {
        isUserSubscribed,
        enablePushNotification,
        disablePushNotification,
        hasVapidKey
    };
};
function convertVapidKey(vapidKey) {
    const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
    const base64 = (vapidKey + padding)
        .replaceAll('-', '+')
        .replaceAll('_', '/');
    const rawData = globalThis.window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.codePointAt(i) || 0;
    }
    return outputArray;
}
async function syncSubscription(subscription, isSubscribed, userId) {
    const platformHost = await ConfigurationAPI.getPlatformHost();
    if (!platformHost || !isValidHttpUrl(platformHost)) {
        console.error('[RBAC Notifications] Invalid PLATFORM_HOST URL');
        return;
    }
    try {
        await NotificationAPI.syncSubscription(subscription, isSubscribed, userId, platformHost);
    }
    catch (error) {
        const action = isSubscribed ? 'Registration' : 'Unregistration';
        console.error(`[RBAC Notifications] ${action} failed:`, error);
    }
}
